import { borderStyle, flexboxStyle, paddingStyle, transitionStyle } from "./styles.mjs";
import { useTheme } from "./ThemeProvider.mjs";
import { useStyles } from "purse-styles";
import React, { forwardRef } from "react";
import { jsx } from "react/jsx-runtime";

//#region src/Flex.tsx
function removeUndefined(object) {
	return Object.fromEntries(Object.entries(object).filter(([_, value]) => value !== void 0));
}
function flexStylesToCss(props, theme) {
	return removeUndefined({
		flexDirection: props.column !== void 0 ? props.column ? "column" : "row" : void 0,
		gap: props.gap ? theme.spacing[props.gap] : void 0,
		alignItems: props.align,
		justifyContent: props.justify,
		flexWrap: props.wrap === true ? "wrap" : props.wrap,
		padding: props.padding ? theme.spacing[props.padding] : void 0,
		paddingTop: props.paddingVertical ? theme.spacing[props.paddingVertical] : void 0,
		paddingBottom: props.paddingVertical ? theme.spacing[props.paddingVertical] : void 0,
		paddingLeft: props.paddingHorizontal ? theme.spacing[props.paddingHorizontal] : void 0,
		paddingRight: props.paddingHorizontal ? theme.spacing[props.paddingHorizontal] : void 0,
		width: props.width ? theme.spacing[props.width] : void 0,
		height: props.height ? theme.spacing[props.height] : void 0,
		boxShadow: props.shadow === true ? theme.shadows.default : props.shadow ? theme.shadows[props.shadow] : void 0,
		...props.style
	});
}
const Flex = forwardRef(({ flex, basis, grow, shrink, inline, column, gap, align, justify, wrap, alignSelf, shadow, shadowTint, transition, padding: paddingProp, paddingVertical, paddingHorizontal, borderWidth, borderTopWidth, borderLeftWidth, borderRightWidth, borderBottomWidth, borderColor = "sandA5", borderRadius, width, height, background, overflow, scrollFade, smoothCorners, style: style$1, className = "", onClick, role, "aria-label": ariaLabel, "aria-level": ariaLevel, tabIndex, onKeyDown, mobile, tablet, children }, ref) => {
	const theme = useTheme();
	return /* @__PURE__ */ jsx("div", {
		ref,
		className: `${className} ${useStyles(flexboxStyle({
			flex,
			basis,
			grow,
			shrink,
			alignSelf,
			column,
			gap,
			align,
			justify,
			wrap,
			inline,
			theme
		}), borderStyle({
			width: borderWidth,
			topWidth: borderTopWidth,
			leftWidth: borderLeftWidth,
			rightWidth: borderRightWidth,
			bottomWidth: borderBottomWidth,
			color: borderColor,
			theme
		}), paddingStyle({
			all: paddingProp,
			vertical: paddingVertical,
			horizontal: paddingHorizontal,
			theme
		}), transition ? transitionStyle({
			transition,
			theme
		}) : {}, {
			boxShadow: shadowTint ? theme.shadows.tinted(theme.colors[shadowTint]) : shadow === true ? theme.shadows.default : shadow ? theme.shadows[shadow] : void 0,
			backgroundColor: background ? theme.colors[background] : void 0,
			width: width ? theme.spacing[width] : void 0,
			height: height ? theme.spacing[height] : void 0,
			borderRadius: borderRadius ? theme.borderRadiuses[borderRadius] : void 0,
			overflowX: overflow === "x" || overflow === "xy" ? "auto" : overflow === "hidden" ? "hidden" : void 0,
			overflowY: overflow === "y" || overflow === "xy" ? "auto" : overflow === "hidden" ? "hidden" : void 0,
			...(() => {
				if (!scrollFade || overflow !== "y" && overflow !== "xy") return {};
				const fadeValue = scrollFade === true ? "both" : scrollFade;
				const fadeSize = "24px";
				let gradient;
				if (fadeValue === "top") gradient = `linear-gradient(to bottom, transparent 0%, black ${fadeSize}, black 100%)`;
				else if (fadeValue === "bottom") gradient = `linear-gradient(to bottom, black 0%, black calc(100% - ${fadeSize}), transparent 100%)`;
				else gradient = `linear-gradient(to bottom, transparent 0%, black ${fadeSize}, black calc(100% - ${fadeSize}), transparent 100%)`;
				return {
					maskImage: gradient,
					WebkitMaskImage: gradient
				};
			})(),
			...smoothCorners ? {
				cornerShape: "superellipse",
				WebkitCornerShape: "superellipse"
			} : {}
		}, style$1 || {})} ${useStyles(tablet ? { [`@media (max-width: ${theme.breakpoints.tablet}px)`]: flexStylesToCss(tablet, theme) } : {})} ${useStyles(mobile ? { [`@media (max-width: ${theme.breakpoints.mobile}px)`]: flexStylesToCss(mobile, theme) } : {})}`.trim(),
		onClick,
		role,
		"aria-label": ariaLabel,
		"aria-level": ariaLevel,
		tabIndex,
		onKeyDown,
		children
	});
});
function spacerStylesToCss(props, theme) {
	return removeUndefined({
		height: props.height ? theme.spacing[props.height] : void 0,
		width: props.width ? theme.spacing[props.width] : void 0,
		...props.style
	});
}
const Spacer = forwardRef(({ flex, basis, grow, shrink, height, width, style: style$1, mobile, tablet }, ref) => {
	const theme = useTheme();
	return /* @__PURE__ */ jsx("div", {
		ref,
		className: `${useStyles({
			flex: flex === true ? `1 1 auto` : flex,
			height: height ? theme.spacing[height] : void 0,
			width: width ? theme.spacing[width] : void 0,
			flexBasis: basis,
			flexGrow: grow === true ? 1 : grow,
			flexShrink: shrink === true ? 1 : shrink === void 0 && (height || width) ? 0 : shrink,
			...style$1
		})} ${useStyles(tablet ? { [`@media (max-width: ${theme.breakpoints.tablet}px)`]: spacerStylesToCss(tablet, theme) } : {})} ${useStyles(mobile ? { [`@media (max-width: ${theme.breakpoints.mobile}px)`]: spacerStylesToCss(mobile, theme) } : {})}`.trim()
	});
});

//#endregion
export { Flex, Spacer };